import React, { useState } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
    BoxSkeleton,
    CardSection,
    SkeletonLoader,
    Timeline,
    TimelineStepProps,
    useModal,
} from '@europrocurement/flexy-components';
import { type Contact, type DossierOpenSearch } from '@europrocurement/l2d-domain';
import { REALM_INTERN, UseKeycloakCheckRole } from '@europrocurement/l2d-keycloak';
import { useUserContactCard } from '@europrocurement/l2d-modules/modules/Utilisateur';
import { User, useUsersCtx } from '@europrocurement/l2d-modules/providers';
import { useUtilisateur } from '@europrocurement/l2d-modules/hooks/useUtilisateur';
import ContactCard from './fragments/ContactCard';
import CommercialCard from './fragments/CommercialCard';
import FormalisteCard from './fragments/FormalisteCard';
import PriorityCard from './fragments/PriorityCard';
import UpdateDateCard from './fragments/UpdateDateCard';
import FormalisteAssignModal from '../../../../Shared/FormalisteAssignModal/FormalisteAssignModal';
import TimeLineWithValidators from '../TimeLineWithValidators/TimeLineWithValidators';

export type DossierInformationsProps = {
    steps: Array<TimelineStepProps>;
    dossierId: number;
    contactNom: string;
    contactPrenom: string;
    contactUserIdentifier: string;
    contactId?: number;
    commercialNom: string;
    commercialPrenom: string;
    commercialUserIdentifier: string;
    commercialId?: number;
    formalisteId: number;
    formalisteNom: string;
    formalistePrenom: string;
    formalisteUserIdentifier: string;
    critical: boolean;
    dateUpdate: string;
    prescripteurContacts?: Contact[];
    onContactChange?: () => void;
    onFormalisteChange?: (formaliste: User, note?: string) => Promise<void>;

    isUpdatingContact?: boolean;
    isUpdatingFormaliste?: boolean;
    isLoadingCommercial?: boolean;
    dossier?: DossierOpenSearch;
    source?: 'FRONT' | 'BACK';
};

/**
 * Composant d'affichage des informations détaillées d'un dossier.
 * Présente la timeline de traitement, les contacts assignés, la priorité et les informations de mise à jour.
 */
const DossierInformations: React.FunctionComponent<DossierInformationsProps> = function (props) {
    const {
        steps,
        contactNom,
        contactPrenom,
        contactUserIdentifier,
        contactId,
        commercialNom,
        commercialPrenom,
        commercialUserIdentifier,
        commercialId,
        formalisteId,
        formalisteNom,
        formalistePrenom,
        formalisteUserIdentifier,
        critical,
        dateUpdate,
        prescripteurContacts = [],
        onContactChange,
        onFormalisteChange,
        isUpdatingContact = false,
        isUpdatingFormaliste = false,
        isLoadingCommercial = false,
        dossier,
        source = 'FRONT',
    } = props;

    const { palette, breakpoints } = useTheme();
    const { modalActions } = useModal();
    const kcCheckRole = UseKeycloakCheckRole();
    const userInterne = kcCheckRole(REALM_INTERN);
    const downMdBreakpoint = useMediaQuery(breakpoints.down('md'));
    const { callUserContactCardModal } = useUserContactCard();
    const [showAllStatus, setShowAllStatus] = useState(false);
    const { getUserById } = useUsersCtx();
    const { searchFormalistes } = useUtilisateur();
    const availableFormalistes = searchFormalistes();

    const timelineStyle = {
        borderBottom: `solid 1px ${palette.grey[300]}`,
        padding: downMdBreakpoint ? '32px' : '16px 32px 32px 32px',
    };

    const infosStyle = {
        display: 'grid',
        gridTemplateColumns: downMdBreakpoint ? '1fr' : 'repeat(5, 1fr)',
        gridTemplateRows: downMdBreakpoint ? 'repeat(5, 1fr)' : '1fr',
        marginTop: '16px',
        width: '100%',
    };

    const infoCardSx = {
        padding: downMdBreakpoint ? '16px 32px' : '8px 32px',
        borderRight: downMdBreakpoint ? 'none' : `solid 1px ${palette.grey[300]}`,
        '&:last-of-type': {
            borderRight: 'none',
        },
    };

    const textStyle = {
        fontSize: '13px',
        fontWeight: '500',
        lineHeight: '19.2px',
        color: palette.grey[500],
    };

    const cardHeader = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="l2dh6">Traitement du dossier</Typography>
            {source === 'BACK' && (
                <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowAllStatus((prev) => !prev)}
                    sx={{
                        alignSelf: 'flex-start',
                        fontSize: '10px',
                        padding: '0 4px',
                        minWidth: 0,
                        color: palette.grey[500],
                        position: 'asbsolute',
                        right: 0,
                        top: 0,
                    }}
                >
                    {showAllStatus ? 'Réduire' : 'Voir tout'}
                </Button>
            )}
        </Box>
    );

    // Props communes pour les cartes
    const baseCardProps = {
        infoCardSx,
        textStyle,
        palette,
        userInterne,
    };

    /**
     * Gère l'ouverture de la modal de formaliste avec modalActions.call()
     */
    const handleFormalisteClick = () => {
        if (!onFormalisteChange) {
            console.warn('onFormalisteChange non défini');
            return;
        }

        const currentFormaliste = formalisteId ? getUserById(formalisteId) : undefined;

        modalActions.call(
            <FormalisteAssignModal
                selectedFormaliste={currentFormaliste}
                onFormalisteAssign={onFormalisteChange}
                title="Assigner un formaliste au dossier"
                submitButtonText="Assigner"
            />,
        );
    };

    const loadingCondition = isUpdatingContact || isLoadingCommercial || isUpdatingFormaliste;

    return (
        <SkeletonLoader
            isLoading={loadingCondition}
            skeleton={
                <BoxSkeleton
                    sx={{
                        height: '250px',
                        marginBottom: '16px',
                        marginTop: '16px',
                    }}
                />
            }
        >
            <CardSection
                header={cardHeader}
                sxHeader={{ padding: '24px 32px 8px 32px' }}
                sx={{ marginTop: '8px' }}
                sxContent={{ padding: '0 0 24px 0' }}
            >
                <Box sx={timelineStyle}>
                    {source === 'BACK' ? (
                        <TimeLineWithValidators
                            steps={steps}
                            showAllStatus={showAllStatus}
                        />
                    ) : (
                        <Timeline steps={steps} />
                    )}
                </Box>

                <Box sx={infosStyle}>
                    <ContactCard
                        {...baseCardProps}
                        contactNom={contactNom}
                        contactPrenom={contactPrenom}
                        contactUserIdentifier={contactUserIdentifier}
                        prescripteurContacts={prescripteurContacts}
                        onContactChange={onContactChange}
                        onClickUser={(e: React.MouseEvent) => {
                            if (contactId && dossier) {
                                e.preventDefault();
                                e.stopPropagation();
                                callUserContactCardModal({
                                    userId: contactId,
                                    displayActionContact: true,
                                    displayUserSchedule: true,
                                    source,
                                    userType: 'CONTACT',
                                    mailSubject: dossier
                                        ? `Information Dossier ${dossier.collectionNumero} ${dossier.numero}`
                                        : '',
                                    tiersIds: [dossier.prescripteurId as number],
                                });
                            }
                        }}
                    />

                    <UpdateDateCard
                        {...baseCardProps}
                        dateUpdate={dateUpdate}
                    />

                    <PriorityCard
                        {...baseCardProps}
                        critical={critical}
                    />

                    <CommercialCard
                        {...baseCardProps}
                        commercialNom={commercialNom}
                        commercialPrenom={commercialPrenom}
                        commercialUserIdentifier={commercialUserIdentifier}
                        onClickUser={(e: React.MouseEvent) => {
                            if (commercialId && dossier) {
                                e.preventDefault();
                                e.stopPropagation();
                                callUserContactCardModal({
                                    userId: commercialId,
                                    displayActionContact: true,
                                    displayUserSchedule: true,
                                    source,
                                    userType: 'UTILISATEUR',
                                    mailSubject: dossier
                                        ? `Information Dossier ${dossier.collectionNumero} ${dossier.numero}`
                                        : '',
                                    tiersIds: [dossier.prescripteurId as number],
                                });
                            }
                        }}
                    />

                    <FormalisteCard
                        {...baseCardProps}
                        formalisteId={formalisteId}
                        formalisteNom={formalisteNom}
                        formalistePrenom={formalistePrenom}
                        formalisteUserIdentifier={formalisteUserIdentifier}
                        availableFormalistes={availableFormalistes}
                        onFormalisteChange={handleFormalisteClick}
                        onClickUser={(e: React.MouseEvent) => {
                            if (contactId && dossier) {
                                e.preventDefault();
                                e.stopPropagation();
                                callUserContactCardModal({
                                    userId: formalisteId,
                                    displayActionContact: true,
                                    displayUserSchedule: true,
                                    source,
                                    userType: 'UTILISATEUR',
                                    mailSubject: dossier
                                        ? `Information Dossier ${dossier.collectionNumero} ${dossier.numero}`
                                        : '',
                                    tiersIds: [dossier.prescripteurId as number],
                                });
                            }
                        }}
                    />
                </Box>
            </CardSection>
        </SkeletonLoader>
    );
};

export default DossierInformations;
